import {Observable, Subscriber} from 'rxjs';

import {FileExtensionsEnum, FileMimeTypesEnum} from '../../../enums/common';

export class FilesHelper {
    private constructor() {
    }

    static getFileMimeType(fileName: string): string {
        const allowedFileExtensions = [
            FileExtensionsEnum.Pdf,
            FileExtensionsEnum.Jpg,
            FileExtensionsEnum.Jpeg,
            FileExtensionsEnum.Png
        ].join('|');

        const fileExtensionRegex = new RegExp('(?!\\.)(' + allowedFileExtensions + ')$', 'i');
        const fileExtensionMatches = fileName.match(fileExtensionRegex);

        if (!fileExtensionMatches) {
            return FileMimeTypesEnum.PlainText;
        }

        switch (fileExtensionMatches[0].toLowerCase()) {
            case FileExtensionsEnum.Pdf:
                return FileMimeTypesEnum.Pdf;
            case FileExtensionsEnum.Jpg:
            case FileExtensionsEnum.Jpeg:
                return FileMimeTypesEnum.Jpg;
            case FileExtensionsEnum.Png:
                return FileMimeTypesEnum.Png;
            default:
                return FileMimeTypesEnum.PlainText;
        }
    }

    static getFileMimeTypeByDataUrl(dataUrl: string): string {
        const allowedFileTypes = [
            FileMimeTypesEnum.Pdf,
            FileMimeTypesEnum.Jpg,
            FileMimeTypesEnum.Jpeg,
            FileMimeTypesEnum.Png
        ].join('|');

        const fileTypeRegex = new RegExp('(?:' + allowedFileTypes + ')', 'i');
        const fileTypeMatches = dataUrl.match(fileTypeRegex);

        if (!fileTypeMatches) {
            return FileMimeTypesEnum.PlainText;
        }

        return fileTypeMatches[0];
    }

    static convertBase64ToBlob(data: string, contentType = '', sliceSize = 512): Blob {
        const byteCharacters = atob(data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);

            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, {type: contentType});
    }

    static convertBlobToFile(blobFile: Blob, fileName: string): File {
        const blob: any = blobFile;

        blob.lastModifiedDate = new Date();
        blob.name = fileName;

        return blob as File;
    }

    static convertFileToBase64(file: File): Observable<string> {
        return new Observable((observer: Subscriber<string>) => {
            const reader = new FileReader();

            reader.readAsDataURL(file);

            reader.onload = () => observer.next(reader.result as string);
            reader.onerror = () => observer.next(null);
        });
    }

    static convertFileToRawBase64(file: File): Observable<string> {
        return new Observable((observer: Subscriber<string>) => {
            const reader = new FileReader();

            reader.readAsBinaryString(file);

            reader.onload = () => {
                  observer.next(window.btoa(reader.result as string)); 
                  observer.complete();
            };
            reader.onerror = () => { 
                observer.error();
                observer.complete();
            };
        });
    }

    static createBase64DataUrl(data: string, mimeType: string): string {
        return 'data:' + mimeType + ';base64,' + data;
    }

    static getDataFromBase64DataUrl(base64DataUrl: string, mimeType: string): string {
        return base64DataUrl.replace('data:' + mimeType + ';base64,', '');
    }

    static getImageFileMimeTypes(): string[] {
        return [
            FileMimeTypesEnum.Jpg,
            FileMimeTypesEnum.Jpeg,
            FileMimeTypesEnum.Png
        ];
    }

    static getExcelFileMimeTypes(): string[] {
        return [
            FileMimeTypesEnum.Xls,
            FileMimeTypesEnum.Xlsx
        ];
    }

    static triggerDownloadFile(fileName: string, blob: Blob): void {
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
            const link = document.createElement('a');

            document.body.appendChild(link);

            link.style.display = 'none';

            const url = window.URL.createObjectURL(blob);

            link.href = url;
            link.download = fileName;

            link.click();

            window.URL.revokeObjectURL(url);

            document.body.removeChild(link);
        }
    }

    static canFilePreviewed(fileName: string): boolean {
        const allowedFileExtensions = [
            FileExtensionsEnum.Pdf,
            FileExtensionsEnum.Jpg,
            FileExtensionsEnum.Jpeg,
            FileExtensionsEnum.Png
        ].join('|');

        const fileExtensionRegex = new RegExp('(?!\\.)(' + allowedFileExtensions + ')$', 'i');
        const fileExtensionMatches = fileName.match(fileExtensionRegex);

        return (typeof fileExtensionMatches[0] !== 'undefined');
    }
}
